import { CHECKLIST_ITEM_STATUS_TYPE } from "@/other/constants/OtherKeys.jsx";

export type ChecklistRecurrence = "none" | "daily" | "weekly" | "monthly";

/** Board / entity checklist (plain API data). */
export type Checklist = {
  id: number | string;
  title: string;
  taskId?: number | string | null;
  resourceId?: number | string | null;
  clientId?: number | string | null;
  archived: boolean;
  createdAt?: Date | string | null;
  updatedAt?: Date | string | null;
  creator?: number;
  updater?: number;
  order?: number;
  canEdit?: number;
  canView?: number;
  assignedBoardUsers: number[];
  recurrence?: ChecklistRecurrence | string;
  recurrenceWeekdays?: number[] | null;
  nudgeDate?: string | Date | null;
  /** List/summary fields from board or entity checklist APIs. */
  totalItems?: number;
  itemsLeft?: number;
  entityTitle?: string;
  uncheckedItemsCount?: number;
  checkedItems?: number;
};

/** Checklist line item (plain API data). */
export type ChecklistItem = {
  id: number | string;
  description: string;
  status: number;
  /** True only when status is Completed (2); from retrieve/tick responses. */
  checked?: boolean;
  checkedBy?: string | null;
  checkedDate?: string | null;
  checklistId: number | string;
  archived: boolean;
  order: number;
  comment?: string | null;
  createdAt?: Date | string | null;
  image?: string;
  thumbnailUrl?: string;
  /** Parent checklist for grant checks — attached by hooks/adapters. */
  checklist?: Checklist;
};

/** Local ids for items/checklists not saved to the API yet; the `_` prefix tells them apart from server ids. */
let draftSeq = 0;

export function nextDraftId(prefix = "checklist"): string {
  draftSeq += 1;
  return `_${prefix}_${Date.now()}_${draftSeq}`;
}

export function isDraftId(id: unknown): boolean {
  return id == null || String(id).startsWith("_");
}

export function isChecklistItemChecked(item: Pick<ChecklistItem, "status" | "checked">): boolean {
  if (item.checked === true) return true;
  if (item.checked === false) return false;
  return item.status === CHECKLIST_ITEM_STATUS_TYPE.COMPLETED;
}

export function createChecklist(partial: Partial<Checklist> & { title?: string } = {}): Checklist {
  return {
    id: partial.id ?? nextDraftId("checklist"),
    title: partial.title ?? "",
    taskId: partial.taskId ?? null,
    resourceId: partial.resourceId ?? null,
    clientId: partial.clientId ?? null,
    archived: Boolean(partial.archived),
    createdAt: partial.createdAt ?? null,
    updatedAt: partial.updatedAt ?? null,
    creator: partial.creator,
    updater: partial.updater,
    order: partial.order ?? 0,
    canEdit: partial.canEdit,
    canView: partial.canView,
    assignedBoardUsers: [...(partial.assignedBoardUsers ?? [])],
    recurrence: partial.recurrence ?? "none",
    recurrenceWeekdays: partial.recurrenceWeekdays ?? null,
    nudgeDate: partial.nudgeDate ?? null,
    totalItems: partial.totalItems,
    itemsLeft: partial.itemsLeft,
    entityTitle: partial.entityTitle,
    uncheckedItemsCount: partial.uncheckedItemsCount,
    checkedItems: partial.checkedItems
  };
}

export function createChecklistItem(
  partial: Partial<ChecklistItem> & { checklistId: number | string }
): ChecklistItem {
  const status = partial.status ?? CHECKLIST_ITEM_STATUS_TYPE.UNSEEN;
  const checked =
    partial.checked !== undefined ? Boolean(partial.checked) : status === CHECKLIST_ITEM_STATUS_TYPE.COMPLETED;
  return {
    id: partial.id ?? nextDraftId("checklistitem"),
    description: partial.description ?? "",
    status,
    checked,
    checkedBy: partial.checkedBy ?? null,
    checkedDate: partial.checkedDate ?? null,
    checklistId: partial.checklistId,
    archived: Boolean(partial.archived),
    order: partial.order ?? 0,
    comment: partial.comment ?? null,
    createdAt: partial.createdAt ?? null,
    image: partial.image,
    thumbnailUrl: partial.thumbnailUrl,
    checklist: partial.checklist
  };
}
